/**
 * Horizon – aktive Variantenauswahl erzwingen
 *
 * - Technische Vorauswahl bleibt bestehen.
 * - Vorausgewählte Optionen sehen zunächst neutral aus.
 * - Jede Optionsgruppe muss einmal aktiv angeklickt werden.
 * - Auch ein Klick auf die bereits vorausgewählte erste Option zählt.
 * - Add-to-Cart bleibt gesperrt, bis alle Gruppen bestätigt wurden.
 */

const selectionState = new WeakMap();
const observedScopes = new WeakSet();

/* --------------------------------------------------
 * Hilfsfunktionen
 * -------------------------------------------------- */

function getProductScope(element) {
  return (
    element.closest('product-information') ||
    element.closest('featured-product-information') ||
    element.closest('quick-add-dialog') ||
    element.closest('product-card') ||
    element.closest('.shopify-section')
  );
}

function getProductFormForPicker(picker) {
  const scope = getProductScope(picker);

  return scope?.querySelector('product-form-component') || null;
}

function getPickerForProductForm(productForm) {
  const scope = getProductScope(productForm);

  return scope?.querySelector('variant-picker') || null;
}

function getTouchedGroups(productForm) {
  let touched = selectionState.get(productForm);

  if (!touched) {
    touched = new Set();
    selectionState.set(productForm, touched);
  }

  return touched;
}

/**
 * Ermittelt jede auswählbare Optionsgruppe separat.
 *
 * Beispiele:
 * - Farbe
 * - Magnet / ohne Magnet
 * - Klebeband / ohne Klebeband
 */
function getOptionGroups(picker) {
  const groups = [];

  picker.querySelectorAll('fieldset').forEach((fieldset, index) => {
    const radios = Array.from(
      fieldset.querySelectorAll('input[type="radio"]')
    );

    if (radios.length < 2) return;

    const groupIdentifier =
      radios[0].dataset.inputId?.split('-')[0] ||
      radios[0].name ||
      index;

    groups.push({
      key: `radio:${groupIdentifier}`,
      container: fieldset,
      controls: radios,
    });
  });

  picker.querySelectorAll('select').forEach((select, index) => {
    if (select.options.length < 2) return;

    const groupIdentifier =
      select.options[0]?.dataset.inputId?.split('-')[0] ||
      select.name ||
      index;

    groups.push({
      key: `select:${groupIdentifier}`,
      container:
        select.closest('.variant-option') ||
        select.parentElement ||
        select,
      controls: [select],
    });
  });

  return groups;
}

function getGroupForControl(picker, control) {
  return getOptionGroups(picker).find((group) =>
    group.controls.includes(control)
  );
}

function hasCompletedSelection(productForm, picker) {
  const groups = getOptionGroups(picker);

  // Produkte ohne auswählbare Varianten bleiben unverändert.
  if (groups.length === 0) return true;

  const touched = getTouchedGroups(productForm);

  return groups.every((group) => touched.has(group.key));
}

/* --------------------------------------------------
 * Optische Darstellung
 * -------------------------------------------------- */

function updateVisualState(productForm, picker) {
  const touched = getTouchedGroups(productForm);
  const groups = getOptionGroups(picker);

  groups.forEach((group) => {
    group.container.dataset.customerSelected = touched.has(group.key)
      ? 'true'
      : 'false';
  });

  picker.toggleAttribute(
    'data-awaiting-customer-selection',
    !hasCompletedSelection(productForm, picker)
  );
}

/**
 * Horizon ersetzt nach einem Variantenwechsel Teile des Markups.
 * Der MutationObserver überträgt den Status direkt auf das neue Markup,
 * ohne mehrere verzögerte Aktualisierungen.
 */
function observePickerUpdates(productForm) {
  const scope = getProductScope(productForm);

  if (!(scope instanceof HTMLElement)) return;
  if (observedScopes.has(scope)) return;

  observedScopes.add(scope);

  const observer = new MutationObserver((mutations) => {
    const markupChanged = mutations.some(
      (mutation) =>
        mutation.type === 'childList' &&
        (mutation.addedNodes.length > 0 ||
          mutation.removedNodes.length > 0)
    );

    if (!markupChanged) return;

    const currentForm =
      scope.querySelector('product-form-component');

    if (!(currentForm instanceof HTMLElement)) return;

    /*
     * Falls Horizon das Formular selbst ersetzt,
     * den bisherigen Auswahlstatus übertragen.
     */
    if (
      currentForm !== productForm &&
      !selectionState.has(currentForm)
    ) {
      const previousState = selectionState.get(productForm);

      if (previousState) {
        selectionState.set(
          currentForm,
          new Set(previousState)
        );
      }
    }

    const currentPicker =
      getPickerForProductForm(currentForm);

    if (currentPicker instanceof HTMLElement) {
      updateVisualState(currentForm, currentPicker);
    }
  });

  observer.observe(scope, {
    childList: true,
    subtree: true,
  });
}

/* --------------------------------------------------
 * Popup
 * -------------------------------------------------- */

function showSelectionPopup() {
  let popup = document.querySelector(
    '[data-variant-selection-popup]'
  );

  if (!popup) {
    popup = document.createElement('div');

    popup.dataset.variantSelectionPopup = '';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute(
      'aria-labelledby',
      'variant-selection-popup-title'
    );

    popup.innerHTML = `
      <div class="variant-selection-popup__backdrop"></div>

      <div class="variant-selection-popup__dialog">
        <button
          type="button"
          class="variant-selection-popup__close"
          aria-label="Fenster schließen"
        >
          ×
        </button>

        <h2 id="variant-selection-popup-title">
          Bitte alle Optionen auswählen
        </h2>

        <p>
          Bitte vervollständige zuerst deine Auswahl, bevor du den Artikel in den Warenkorb legst.
        </p>

        <button
          type="button"
          class="variant-selection-popup__button"
        >
          Verstanden
        </button>
      </div>
    `;

    document.body.appendChild(popup);

    const closePopup = () => {
      popup.classList.remove('is-visible');
      document.body.style.overflow = '';
    };

    popup
      .querySelector('.variant-selection-popup__backdrop')
      ?.addEventListener('click', closePopup);

    popup
      .querySelector('.variant-selection-popup__close')
      ?.addEventListener('click', closePopup);

    popup
      .querySelector('.variant-selection-popup__button')
      ?.addEventListener('click', closePopup);

    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        popup.classList.contains('is-visible')
      ) {
        closePopup();
      }
    });
  }

  popup.classList.add('is-visible');
  document.body.style.overflow = 'hidden';

  popup
    .querySelector('.variant-selection-popup__button')
    ?.focus();
}

/* --------------------------------------------------
 * Styles
 * -------------------------------------------------- */

function addStyles() {
  if (
    document.querySelector(
      '[data-required-variant-selection-styles]'
    )
  ) {
    return;
  }

  const style = document.createElement('style');

  style.dataset.requiredVariantSelectionStyles = '';

  style.textContent = `
    /*
     * Technische Vorauswahl neutral darstellen,
     * solange die Gruppe noch nicht aktiv angeklickt wurde.
     */

    variant-picker
      fieldset[data-customer-selected="false"]
      .variant-option__button-label:has(input:checked) {
      background: transparent !important;
      color: inherit !important;
    }

    variant-picker
      fieldset[data-customer-selected="false"]
      input:checked
      ~ .variant-option__button-label__pill {
      opacity: 0 !important;
      visibility: hidden !important;
      background: transparent !important;
      transform: none !important;
    }

    variant-picker
      fieldset[data-customer-selected="false"]
      input:checked
      ~ .variant-option__button-label__text {
      color: inherit !important;
    }

    /*
     * Ruhiger Variantenwechsel.
     */

    variant-picker .variant-option__button-label,
    variant-picker .variant-option__button-label__pill,
    variant-picker .variant-option__button-label__text {
      transition:
        background-color 0.12s ease,
        color 0.12s ease,
        opacity 0.12s ease,
        border-color 0.12s ease !important;
    }

    /*
     * Popup.
     */

    [data-variant-selection-popup] {
      position: fixed;
      inset: 0;
      z-index: 999999;
      display: none;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }

    [data-variant-selection-popup].is-visible {
      display: flex;
    }

    .variant-selection-popup__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(2px);
    }

    .variant-selection-popup__dialog {
      position: relative;
      z-index: 1;
      width: min(100%, 420px);
      padding: 32px 28px 28px;
      border-radius: 18px;
      background: #ffffff;
      color: #1a1a1a;
      box-shadow: 0 24px 70px rgba(0, 0, 0, 0.28);
      text-align: center;
    }

    .variant-selection-popup__dialog h2 {
      margin: 0 0 12px;
      font-size: 22px;
      line-height: 1.25;
    }

    .variant-selection-popup__dialog p {
      margin: 0 0 24px;
      font-size: 16px;
      line-height: 1.5;
    }

    .variant-selection-popup__button {
      min-width: 150px;
      padding: 13px 24px;
      border: 0;
      border-radius: 999px;
      background: #a96b3a;
      color: #ffffff;
      font: inherit;
      font-weight: 600;
      cursor: pointer;
    }

    .variant-selection-popup__button:hover {
      opacity: 0.9;
    }

    .variant-selection-popup__button:focus-visible,
    .variant-selection-popup__close:focus-visible {
      outline: 2px solid #1a1a1a;
      outline-offset: 3px;
    }

    .variant-selection-popup__close {
      position: absolute;
      top: 10px;
      right: 14px;
      width: 36px;
      height: 36px;
      padding: 0;
      border: 0;
      background: transparent;
      color: #1a1a1a;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
    }

    @media (max-width: 480px) {
      .variant-selection-popup__dialog {
        padding: 30px 22px 24px;
        border-radius: 14px;
      }

      .variant-selection-popup__dialog h2 {
        font-size: 20px;
      }

      .variant-selection-popup__dialog p {
        font-size: 15px;
      }
    }
  `;

  document.head.appendChild(style);
}

/* --------------------------------------------------
 * Auswahl erfassen
 * -------------------------------------------------- */

/**
 * Radio-Buttons über Klick erfassen.
 *
 * So zählt auch ein Klick auf die bereits vorausgewählte
 * erste Option als bewusste Auswahl.
 */
document.addEventListener(
  'click',
  (event) => {
    if (!event.isTrusted) return;

    const target = event.target;

    if (!(target instanceof Element)) return;

    const label = target.closest(
      'variant-picker .variant-option__button-label'
    );

    let radio = null;

    if (label) {
      radio = label.querySelector('input[type="radio"]');
    } else if (
      target instanceof HTMLInputElement &&
      target.type === 'radio'
    ) {
      radio = target;
    }

    if (!(radio instanceof HTMLInputElement)) return;

    const picker = radio.closest('variant-picker');

    if (!(picker instanceof HTMLElement)) return;

    const productForm = getProductFormForPicker(picker);

    if (!(productForm instanceof HTMLElement)) return;

    const group = getGroupForControl(picker, radio);

    if (!group) return;

    getTouchedGroups(productForm).add(group.key);
    updateVisualState(productForm, picker);
  },
  true
);

/**
 * Dropdowns über change erfassen.
 */
document.addEventListener(
  'change',
  (event) => {
    if (!event.isTrusted) return;

    const select = event.target;

    if (!(select instanceof HTMLSelectElement)) return;

    const picker = select.closest('variant-picker');

    if (!(picker instanceof HTMLElement)) return;

    const productForm = getProductFormForPicker(picker);

    if (!(productForm instanceof HTMLElement)) return;

    const group = getGroupForControl(picker, select);

    if (!group) return;

    getTouchedGroups(productForm).add(group.key);
    updateVisualState(productForm, picker);
  },
  true
);

/* --------------------------------------------------
 * Warenkorb blockieren
 * -------------------------------------------------- */

function blockAddToCart(event, productForm, picker) {
  if (hasCompletedSelection(productForm, picker)) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  showSelectionPopup();

  return true;
}

/**
 * Klick auf den Add-to-Cart-Button abfangen.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const addButton = target.closest(
      [
        'add-to-cart-component button[ref="addToCartButton"]',
        'product-form-component button[name="add"]',
        'product-form-component button[type="submit"]',
      ].join(',')
    );

    if (!(addButton instanceof HTMLButtonElement)) return;

    const productForm = addButton.closest(
      'product-form-component'
    );

    if (!(productForm instanceof HTMLElement)) return;

    const picker = getPickerForProductForm(productForm);

    if (!(picker instanceof HTMLElement)) return;

    blockAddToCart(event, productForm, picker);
  },
  true
);

/**
 * Zusätzliche Absicherung auf Submit-Ebene.
 */
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest(
      'product-form-component'
    );

    if (!(productForm instanceof HTMLElement)) return;

    const picker = getPickerForProductForm(productForm);

    if (!(picker instanceof HTMLElement)) return;

    blockAddToCart(event, productForm, picker);
  },
  true
);

/* --------------------------------------------------
 * Initialisierung
 * -------------------------------------------------- */

function initializeProductForms(root = document) {
  root
    .querySelectorAll('product-form-component')
    .forEach((productForm) => {
      if (!(productForm instanceof HTMLElement)) return;

      const picker = getPickerForProductForm(productForm);

      if (!(picker instanceof HTMLElement)) return;

      updateVisualState(productForm, picker);
      observePickerUpdates(productForm);
    });
}

document.addEventListener('DOMContentLoaded', () => {
  addStyles();
  initializeProductForms();
});

document.addEventListener('shopify:section:load', (event) => {
  const root =
    event.target instanceof Element
      ? event.target
      : document;

  initializeProductForms(root);
});